import { useFormikContext } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Row, Col, Label } from 'reactstrap';
import {
  ROUNDING_CONDITION_OPTIONS,
  ROUNDING_INTERVAL_OPTIONS,
} from 'pages/SettingsV2/SettingsConstants';
import SettingsSelect from 'pages/SettingsV2/SettingsSelect';
import {
  RoundingCondition,
  SettingsFormValues,
} from 'pages/SettingsV2/settingsTypes';
import { useSettings } from 'pages/SettingsV2/SettingsContext';
import SettingsV2SectionCard from 'pages/SettingsV2/SettingsV2SectionCard';

const getRoundingExampleKeys = (
  condition: RoundingCondition,
  interval: number | null
): string[] => {
  if (condition === 0 || interval == null) return [];
  if (condition === 1 && interval === 0.5) {
    return [
      '1.01 - 1.24 rounds to the nearest amount as 1.00',
      '1.25 - 1.49, 1.51 - 1.74 rounds to the nearest amount as 1.50',
      '1.75 - 1.99 rounds to the nearest amount as 2.00',
    ];
  }
  if (condition === 1 && interval === 1) {
    return [
      '1.01 - 1.49 rounds to the nearest amount as 1.00',
      '1.50 - 1.99 rounds to the nearest amount as 2.00',
    ];
  }
  if (condition === 2 && interval === 0.5) {
    return ['1.01 - 1.49 rounds up as 1.50', '1.51 - 1.99 rounds up as 2.00'];
  }
  if (condition === 2 && interval === 1) {
    return ['1.01 - 1.99 rounds up as 2.00'];
  }
  if (condition === 3 && interval === 0.5) {
    return [
      '1.01 - 1.49 rounds down as 1.00',
      '1.51 - 1.99 rounds down as 1.50',
    ];
  }
  if (condition === 3 && interval === 1) {
    return ['1.01 - 1.99 rounds down 1.00'];
  }
  return [];
};

const RoundingSectionV2 = () => {
  const { t } = useTranslation();
  const {
    fieldErrors,
    clearFieldError,
    validateField,
    isSectionAccessible,
    getSectionRestrictionNotice,
  } = useSettings();
  const { values, setFieldValue } = useFormikContext<SettingsFormValues>();
  const { condition, interval } = values.rounding;
  const showInterval = condition !== 0;
  const exampleKeys = getRoundingExampleKeys(condition, interval);
  const accessible = isSectionAccessible('rounding');
  const notice = getSectionRestrictionNotice('rounding');

  return (
    <SettingsV2SectionCard
      sectionKey="rounding"
      title={t('Rounding Settings')}
      icon="ri-coin-line"
      disabled={!accessible}
      notice={notice}
    >
      <div className="mb-3 row">
        <Label
          htmlFor="rounding-v2-condition"
          className="col-sm-3 col-form-label"
        >
          {t('Rounding Method')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="rounding-v2-condition"
            value={condition}
            onChange={(value) => {
              const next = Number(value) as RoundingCondition;
              setFieldValue('rounding.condition', next);
              clearFieldError('rounding.interval');
              if (next !== 0 && interval == null) {
                setFieldValue(
                  'rounding.interval',
                  ROUNDING_INTERVAL_OPTIONS[0]
                );
              }
            }}
            options={ROUNDING_CONDITION_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onBlur={() => validateField('rounding.interval')}
          />
        </Col>
      </div>

      {showInterval ? (
        <div className="mb-3 row">
          <Label
            htmlFor="rounding-v2-interval"
            className="col-sm-3 col-form-label"
          >
            {t('Rounding Interval')}
          </Label>
          <Col sm={9}>
            <SettingsSelect
              inputId="rounding-v2-interval"
              value={interval ?? ''}
              onChange={(value) => {
                setFieldValue('rounding.interval', Number(value));
                clearFieldError('rounding.interval');
              }}
              options={ROUNDING_INTERVAL_OPTIONS.map((option) => ({
                value: option,
                label: option.toFixed(2),
              }))}
              onBlur={() => validateField('rounding.interval')}
            />
            {fieldErrors['rounding.interval'] ? (
              <div className="text-danger mt-1 err-txt">
                {t(fieldErrors['rounding.interval'] as string)}
              </div>
            ) : null}
          </Col>
        </div>
      ) : null}

      {showInterval ? (
        <Row>
          <Col sm={3} />

          <Col sm={9}>
            <Alert color="danger" className="fs-13 mb-3">
              <Trans
                i18nKey="Rounding applies to cash payments only"
                components={{ strong: <strong /> }}
              />
            </Alert>
          </Col>
        </Row>
      ) : null}

      {exampleKeys.length > 0 ? (
        <div className="row">
          <Label className="col-sm-3 col-form-label">{t('Example:')}</Label>
          <Col sm={9} className="fs-13 text-muted">
            {exampleKeys.map((key) => (
              <div key={key}>{t(key)}</div>
            ))}
          </Col>
        </div>
      ) : null}
    </SettingsV2SectionCard>
  );
};

export default RoundingSectionV2;
